import { isHtmlDateValue, normalizeCalendarDateKey, type CalendarDateKey } from "@shared/ui/input";
import type { CustomFieldMapSpec } from "./types";

/** Опции {@link nullableString}. */
export type NullableStringMapOptions = {
    /**
     * Значение в домене при пустой строке формы.
     * @default `"null"` — для полей `string | null` в API.
     * `"undefined"` — если в частичном патче нужно именно `undefined`, а не `null`.
     */
    whenEmpty?: "null" | "undefined";
    /**
     * Если `true`, пустота определяется по `value.trim() === ""` (как «нет текста»), а в домен уходит исходная строка,
     * если она не пуста по этому правилу — без обрезки краёв.
     */
    trim?: boolean;
};

/**
 * Маппинг «домен `string | null` (или опциональная строка) ↔ форма `string`»:
 * в форме пустая строка означает «не задано». Опция {@link NullableStringMapOptions.trim trim} задаёт проверку пустоты через `trim`.
 *
 * @template TDomain тип доменной модели
 * @template TKey ключ строкового поля в домене
 * @param key ключ поля в доменной модели (`BluprintNumber`, `ExternalSign`, …)
 * @param options см. {@link NullableStringMapOptions}
 * @returns объект `map` для поля `type: "string"` в `defineFormConfig`
 */
export function nullableString<TDomain extends object, TKey extends keyof TDomain>(
    key: TKey,
    options?: NullableStringMapOptions,
): CustomFieldMapSpec<object, TDomain, string, unknown> {
    const whenEmpty = options?.whenEmpty ?? "null";
    const trim = options?.trim === true;
    return {
        from: (domain) => {
            const v = (domain as Record<string, unknown>)[key as string];
            return typeof v === "string" ? v : "";
        },
        to: (value) => {
            const normalized = typeof value === "string" ? value : "";
            const isEmpty = trim ? normalized.trim() === "" : normalized === "";
            if (!isEmpty) {
                return { [key]: normalized } as Partial<TDomain>;
            }
            return (whenEmpty === "undefined" ? { [key]: undefined } : { [key]: null }) as Partial<TDomain>;
        },
    };
}

/**
 * Маппинг «домен `T` (или `T | null`) ↔ форма `T | null`» с fallback-значением при записи в домен. \
 * Универсальный адаптер: работает для `number`, `string`, `boolean` и других типов.
 *
 * @template TDomain тип доменной модели
 * @template TKey ключ поля в доменной модели
 * @param key ключ поля в доменной модели (`Weight`, `TypeByUse`, …)
 * @param fallback значение, записываемое в домен, когда форма содержит `null`
 * @returns объект `map` для nullable-поля в `defineFormConfig`
 *
 * @example
 * ```ts
 * weight: {
 *     type: "number",
 *     nullable: true,
 *     map: withDefault("Weight", 0),
 * }
 * ```
 *
 * @example
 * ```ts
 * kindByUse: {
 *     type: "number",
 *     nullable: true,
 *     map: withDefault("TypeByUse", 0),
 * }
 * ```
 */
export function withDefault<TDomain extends object, TKey extends keyof TDomain>(
    key: TKey,
    fallback: NonNullable<TDomain[TKey]>,
): CustomFieldMapSpec<object, TDomain, TDomain[TKey] | null, unknown> {
    return {
        from: (domain) => domain[key],
        to: (value) => ({ [key]: value ?? fallback }) as Partial<TDomain>,
    };
}

/**
 * Базовая форма значения для `LIKE`-фильтров (`val` + `_query`).
 */
type LikeQuery = {
    /** Значение, которое отправляется в API (обычно с `%...%`). */
    val: string;
    /** «Чистое» значение, которое показывается в UI. */
    _query?: string;
    /** Режим учёта регистра. */
    cs?: "case_independent" | "case_dependent";
};

/**
 * Значение «от / до» для range-фильтров.
 * Отсутствие ключа или `undefined` у границы означает «не задано».
 * @see {@link filterFieldAdapters.numberRange}
 */
export type NumberRangeValue = {
    /** Начало диапазона (нет поля — без нижней границы). */
    from?: number;
    /** Конец диапазона (нет поля — без верхней границы). */
    to?: number;
};

/** Границы интервала дат в фильтре OMP (`from` / `to` как date-only строки). */
type OmpFilterDateLike = {
    from?: string;
    to?: string;
};

export type DateOnlyFieldMapOptions = {
    /**
     * Значение в домене при пустой дате формы.
     * @default `"undefined"` — для partial patch и фильтров.
     */
    whenEmpty?: "null" | "undefined";
};

function readApiDateOnly(raw: unknown): CalendarDateKey | null {
    if (typeof raw !== "string") return null;
    const trimmed = raw.trim();
    if (trimmed === "") return null;

    const strict = normalizeCalendarDateKey(trimmed);
    if (strict != null) return strict;

    // Legacy API historically returned ISO-like strings for date-only fields.
    // Keep this tolerance inside map adapters, not in shared UI.
    return normalizeCalendarDateKey(trimmed.slice(0, 10));
}

/**
 * Валидация пары дат в filter-форме: нижняя граница не позже верхней.
 *
 * @returns текст ошибки или `null`, если проверка не применима или значения согласованы
 */
export function filterDateIntervalFromNotAfterTo(from: string | null, to: string | null): string | null {
    if (from == null || to == null) return null;
    const a = String(from).trim();
    const b = String(to).trim();
    if (a === "" || b === "") return null;
    if (isHtmlDateValue(a) === false || isHtmlDateValue(b) === false) return null;
    return a > b ? "Дата «с» не может быть позже даты «по»" : null;
}

/**
 * Валидация пары дат в filter-форме: верхняя граница не раньше нижней.
 *
 * @returns текст ошибки или `null`, если проверка не применима или значения согласованы
 */
export function filterDateIntervalToNotBeforeFrom(to: string | null, from: string | null): string | null {
    if (to == null || from == null) return null;
    const a = String(from).trim();
    const b = String(to).trim();
    if (a === "" || b === "") return null;
    if (isHtmlDateValue(a) === false || isHtmlDateValue(b) === false) return null;
    return b < a ? "Дата «по» не может быть раньше даты «с»" : null;
}

/**
 * Набор date-only адаптеров для `map` декларативных форм.
 *
 * Форма хранит календарный ключ `yyyy-MM-dd`; API по умолчанию получает такой же date-only ключ.
 * Legacy ISO-like строки читаются толерантно только на входе, чтобы не протаскивать instant-формат в UI/RHF.
 */
export const fieldMapDateAdapters = {
    dateOnlyField<TDomain extends object, TKey extends keyof TDomain>(
        key: TKey,
        options?: DateOnlyFieldMapOptions,
    ): CustomFieldMapSpec<object, TDomain, CalendarDateKey | null, unknown> {
        const whenEmpty = options?.whenEmpty ?? "undefined";
        return {
            from: (domain) => readApiDateOnly((domain as Record<string, unknown>)[key as string]),
            to: (value) => {
                const dateKey = typeof value === "string" ? normalizeCalendarDateKey(value) : null;
                if (dateKey != null) return { [key]: dateKey } as Partial<TDomain>;
                return (whenEmpty === "null" ? { [key]: null } : { [key]: undefined }) as Partial<TDomain>;
            },
        };
    },
};

/**
 * Хелпер для массивов ссылочных сущностей:
 * - `from`: читает массив сущностей из `sourceKey`;
 * - `to`: пишет и сущности в `sourceKey`, и массив кодов в `codesKey`.
 *
 * @template TDomain тип доменной модели
 * @template TItem тип элемента массива
 * @template TSourceKey ключ массива сущностей в доменной модели
 * @template TCodesKey ключ массива кодов в доменной модели
 * @param sourceKey ключ, где хранятся сущности (`UserGroups`, `Divisions`, ...)
 * @param codesKey ключ, где хранятся коды (`GroupCodes`, `DivisionCodes`, ...)
 * @param getCode accessor для извлечения кода из элемента
 * @returns объект `map` для поля `array` в `defineFormConfig`
 */
export function refCodes<TDomain extends object, TItem, TSourceKey extends keyof TDomain, TCodesKey extends keyof TDomain>(
    sourceKey: TSourceKey,
    codesKey: TCodesKey,
    getCode: (item: TItem) => unknown,
): CustomFieldMapSpec<object, TDomain, TItem[], unknown> {
    return {
        from: (domain) => ((domain as Record<string, unknown>)[sourceKey as string] as TItem[] | null | undefined) ?? [],
        to: (items) =>
            ({
                [sourceKey]: items,
                [codesKey]: items.map(getCode),
            }) as Partial<TDomain>,
    };
}

/** Опции {@link filterFieldAdapters.ompSearchString}. */
export type OmpSearchStringOptions = {
    /**
     * Добавлять `cs: "case_independent"` в запрос. \
     * Используется для полей, где бэкенд поддерживает регистронезависимый поиск.
     * @default false
     */
    caseIndependent?: boolean;
    /**
     * Оборачивать введённое значение в `%…%` для подстановочного поиска в `val`.
     * Если `false`, в домен уходит `{ val: trimmed, _query: trimmed }` без добавления `%`.
     * @default true
     */
    wrapWithPercent?: boolean;
};

/**
 * Набор адаптеров для map-полей фильтр-форм.
 *
 * @example
 * ```ts
 * const config = defineFilterConfig<MyFilterForm, MyFilterApi>({
 *     fields: {
 *         sign:  { type: "string", label: "Код",  map: filterFieldAdapters.ompSearchString("Sign", { caseIndependent: true }) },
 *         type:  { type: "number", nullable: true, label: "Тип", map: filterFieldAdapters.entityCode("Type") },
 *         codes: { type: "array",  label: "Статусы", map: filterFieldAdapters.entityCodes("StateCodes") },
 *     },
 * });
 * ```
 */
export const filterFieldAdapters = {
    /**
     * Строковый фильтр OMP (`OMPSearchString`): \
     * `from` — читает `_query` или нормализует `val` (удаляет `%`); \
     * `to` — по умолчанию `{ val: "%…%", _query }` (см. {@link OmpSearchStringOptions.wrapWithPercent}); \
     * пустая строка → ключ не передаётся в домен.
     *
     * @template TDomain тип доменного фильтра
     * @template TKey ключ поля фильтра в доменной модели
     * @param key ключ доменного поля (`LoginName`, `Sign`, …)
     * @param options см. {@link OmpSearchStringOptions}
     * @returns объект `map` для строкового поля filter-формы
     */
    ompSearchString<TDomain extends object, TKey extends keyof TDomain>(
        key: TKey,
        options?: OmpSearchStringOptions,
    ): CustomFieldMapSpec<object, TDomain, string, unknown> {
        const cs = options?.caseIndependent === true ? ("case_independent" as const) : undefined;
        const wrapWithPercent = options?.wrapWithPercent !== false;
        return {
            from: (domain) => {
                const entry = (domain as Record<string, LikeQuery | undefined>)[key as string];
                if (entry == null) return "";
                if (typeof entry._query === "string") return entry._query;
                const val = typeof entry.val === "string" ? entry.val : "";
                return val.replaceAll("%", "");
            },
            to: (value) => {
                const normalized = typeof value === "string" ? value : "";
                const trimmed = normalized.trim();
                if (trimmed === "") {
                    return { [key]: undefined } as Partial<TDomain>;
                }
                const val = wrapWithPercent ? `%${trimmed}%` : trimmed;
                const entry: LikeQuery = { val, _query: trimmed };
                if (cs != null) entry.cs = cs;
                return { [key]: entry } as Partial<TDomain>;
            },
        };
    },

    /**
     * Single-select → optional code. \
     * `from` — читает числовой код из домена (`null` если отсутствует); \
     * `to` — записывает код или `undefined` при `null` (убирает ключ из фильтра).
     *
     * @template TDomain тип доменного фильтра
     * @template TKey ключ поля фильтра в доменной модели
     * @param key ключ доменного поля (`Type`, `TypeByTurn`, …)
     * @returns объект `map` для поля `type: "number", nullable: true`
     */
    entityCode<TDomain extends object, TKey extends keyof TDomain>(
        key: TKey,
    ): CustomFieldMapSpec<object, TDomain, number | null, unknown> {
        return {
            from: (domain) => {
                const v = (domain as Record<string, unknown>)[key as string];
                return typeof v === "number" ? v : null;
            },
            to: (value) => ({ [key]: value ?? undefined }) as Partial<TDomain>,
        };
    },

    /**
     * Multi-select → optional codes array. \
     * `from` — читает массив кодов из домена (пустой массив если отсутствует); \
     * `to` — записывает массив или `undefined` при пустом массиве.
     *
     * @template TDomain тип доменного фильтра
     * @template TKey ключ поля фильтра в доменной модели
     * @param key ключ доменного поля (`StateCodes`, `TareUcCodes`, …)
     * @returns объект `map` для поля `type: "array"`
     */
    entityCodes<TDomain extends object, TKey extends keyof TDomain>(
        key: TKey,
    ): CustomFieldMapSpec<object, TDomain, number[], unknown> {
        return {
            from: (domain) => {
                const v = (domain as Record<string, unknown>)[key as string];
                return Array.isArray(v) ? (v as number[]) : [];
            },
            to: (value) => {
                const normalized = Array.isArray(value) ? value : [];
                return { [key]: normalized.length > 0 ? normalized : undefined } as Partial<TDomain>;
            },
        };
    },

    /**
     * Один выбранный код в UI ↔ массив из одного кода в доменном фильтре (или снятие ключа). \
     * Значение `0` трактуется как «не выбрано» (коды-ссылки OMP).
     *
     * @template TDomain тип доменного фильтра
     * @template TKey ключ поля-массива кодов (`UserCodes`, `DeputyUserCodes`, …)
     * @param key ключ доменного поля
     * @returns объект `map` для поля `type: "number", nullable: true`
     */
    singleCodeArray<TDomain extends object, TKey extends keyof TDomain>(
        key: TKey,
    ): CustomFieldMapSpec<object, TDomain, number | null, unknown> {
        return {
            from: (domain) => {
                const v = (domain as Record<string, unknown>)[key as string];
                if (!Array.isArray(v) || v.length === 0) return null;
                const first = v[0];
                return typeof first === "number" ? first : null;
            },
            to: (value) => {
                if (value == null || value === 0) {
                    return { [key]: undefined } as Partial<TDomain>;
                }
                return { [key]: [value] } as Partial<TDomain>;
            },
        };
    },

    /**
     * Tri-state boolean фильтр (`null` / `true` / `false`). \
     * `from` — читает boolean из домена (`null` если не задан); \
     * `to` — записывает значение или `undefined` при `null` (фильтр снят).
     *
     * @template TDomain тип доменного фильтра
     * @template TKey ключ поля фильтра в доменной модели
     * @param key ключ доменного поля (`InProductionProgram`, `APPRO`, …)
     * @returns объект `map` для поля `type: "boolean", nullable: true`
     */
    boolean<TDomain extends object, TKey extends keyof TDomain>(
        key: TKey,
    ): CustomFieldMapSpec<object, TDomain, boolean | null, unknown> {
        return {
            from: (domain) => {
                const v = (domain as Record<string, unknown>)[key as string];
                return typeof v === "boolean" ? v : null;
            },
            to: (value) => ({ [key]: value ?? undefined }) as Partial<TDomain>,
        };
    },

    /**
     * Числовой диапазон `{ From?, To? }`. \
     * Форма хранит {@link NumberRangeValue} (`from?` / `to?` — только заданные границы). \
     * `to` (mapper) — в домен пишет объект с заполненными полями или `undefined`, если границ нет.
     *
     * @template TDomain тип доменного фильтра
     * @template TKey ключ поля фильтра в доменной модели
     * @param key ключ доменного поля (`ExternalLength`, `ExternalWidth`, …)
     * @returns объект `map` для поля `type: "object"` (часто с `default: {}`)
     */
    numberRange<TDomain extends object, TKey extends keyof TDomain>(
        key: TKey,
    ): CustomFieldMapSpec<object, TDomain, NumberRangeValue, unknown> {
        return {
            from: (domain) => {
                const v = (domain as Record<string, { From?: number; To?: number } | undefined>)[key as string];
                const out: NumberRangeValue = {};
                if (v?.From != null) out.from = v.From;
                if (v?.To != null) out.to = v.To;
                return out;
            },
            to: (value) => {
                const from = value?.from;
                const to = value?.to;
                const hasFrom = from != null;
                const hasTo = to != null;
                if (!hasFrom && !hasTo) {
                    return { [key]: undefined } as Partial<TDomain>;
                }
                const range: { From?: number; To?: number } = {};
                if (hasFrom) range.From = from;
                if (hasTo) range.To = to;
                return { [key]: range } as Partial<TDomain>;
            },
        };
    },

    /**
     * Один «конец» числового диапазона `{ From?, To? }` как плоское `number | null` поле. \
     * Каждая граница — отдельное поле формы, что даёт нативный биндинг через `getNumberInputProps`
     * без ручного `watch` / `setValue` / spread.
     *
     * Оба поля пары пишут в один доменный ключ; при записи адаптер читает значение
     * соседнего поля из `formValues`, поэтому `Object.assign` в `toDomain` даёт корректный результат.
     *
     * @template TDomain тип доменного фильтра
     * @template TKey ключ поля фильтра в доменной модели
     * @template TForm тип значений формы
     * @param key ключ доменного поля (`ExternalLength`, `ExternalWidth`, …)
     * @param end какую границу представляет это поле: `"from"` или `"to"`
     * @param siblingFormKey имя соседнего поля формы (для `"from"` — ключ `"…To"`, и наоборот)
     * @returns объект `map` для поля `type: "number", nullable: true`
     *
     * @example
     * ```ts
     * externalLengthFrom: {
     *     type: "number", nullable: true, label: "Длина с",
     *     map: filterFieldAdapters.numberRangeEnd("ExternalLength", "from", "externalLengthTo"),
     * },
     * externalLengthTo: {
     *     type: "number", nullable: true, label: "Длина по",
     *     map: filterFieldAdapters.numberRangeEnd("ExternalLength", "to", "externalLengthFrom"),
     * },
     * ```
     */
    numberRangeEnd<TDomain extends object, TKey extends keyof TDomain, TForm extends object>(
        key: TKey,
        end: "from" | "to",
        siblingFormKey: keyof TForm & string,
    ): CustomFieldMapSpec<TForm, TDomain, number | null, unknown> {
        return {
            from: (domain) => {
                const v = (domain as Record<string, { From?: number; To?: number } | undefined>)[key as string];
                const raw = end === "from" ? v?.From : v?.To;
                return raw ?? null;
            },
            to: (value, formValues) => {
                const siblingValue = (formValues as Record<string, number | null | undefined>)[siblingFormKey] ?? null;
                const from = end === "from" ? value : siblingValue;
                const to = end === "to" ? value : siblingValue;
                const hasFrom = from != null;
                const hasTo = to != null;
                if (!hasFrom && !hasTo) {
                    return { [key]: undefined } as Partial<TDomain>;
                }
                const range: { From?: number; To?: number } = {};
                if (hasFrom) range.From = from;
                if (hasTo) range.To = to;
                return { [key]: range } as Partial<TDomain>;
            },
        };
    },

    /**
     * Один «конец» интервала дат {@link OmpFilterDateLike} как плоское `string | null` в форме (`yyyy-MM-dd`). \
     * В домен пишет date-only ключи, без преобразования календарного дня в instant.
     *
     * @template TDomain тип доменного фильтра
     * @template TKey ключ поля интервала в домене (`CreateDateInterval`, …)
     * @template TForm тип значений формы
     * @param key ключ доменного поля
     * @param end какую границу представляет поле: `"from"` или `"to"`
     * @param siblingFormKey имя соседнего поля формы
     * @returns объект `map` для поля `type: "date", nullable: true`
     */
    dateIntervalEnd<TDomain extends object, TKey extends keyof TDomain, TForm extends object>(
        key: TKey,
        end: "from" | "to",
        siblingFormKey: keyof TForm & string,
    ): CustomFieldMapSpec<TForm, TDomain, string | null, unknown> {
        return {
            from: (domain) => {
                const v = (domain as Record<string, OmpFilterDateLike | undefined>)[key as string];
                const raw = end === "from" ? v?.from : v?.to;
                if (raw == null || raw === "") return null;
                return readApiDateOnly(raw);
            },
            to: (value, formValues) => {
                const fv = formValues as Record<string, string | null | undefined>;
                const siblingRaw = fv[siblingFormKey];
                const fromRaw = end === "from" ? value : siblingRaw;
                const toRaw = end === "to" ? value : siblingRaw;
                const fromKey = readApiDateOnly(fromRaw);
                const toKey = readApiDateOnly(toRaw);
                if (fromKey == null && toKey == null) {
                    return { [key]: undefined } as Partial<TDomain>;
                }
                const interval: OmpFilterDateLike = {};
                if (fromKey != null) interval.from = fromKey;
                if (toKey != null) interval.to = toKey;
                return { [key]: interval } as Partial<TDomain>;
            },
        };
    },

    /**
     * Одна граница {@link OmpFilterDateLike} в отдельном доменном поле (`StartDate.from`, `FinishDate.to`, …). \
     * Для пар «с/по» на одном ключе используй {@link filterFieldAdapters.dateIntervalEnd}.
     */
    ompFilterDateBound<TDomain extends object, TKey extends keyof TDomain>(
        key: TKey,
        bound: "from" | "to",
    ): CustomFieldMapSpec<object, TDomain, string | null, unknown> {
        return {
            from: (domain) => {
                const v = (domain as Record<string, OmpFilterDateLike | undefined>)[key as string];
                const raw = bound === "from" ? v?.from : v?.to;
                if (raw == null || raw === "") return null;
                return readApiDateOnly(raw);
            },
            to: (value) => {
                const boundKey = readApiDateOnly(value);
                if (boundKey == null) {
                    return { [key]: undefined } as Partial<TDomain>;
                }
                return (bound === "from" ? { [key]: { from: boundKey } } : { [key]: { to: boundKey } }) as Partial<TDomain>;
            },
        };
    },
};
